import React, { useRef, useState } from 'react';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { resizeImageFile } from '../utils/imageResize';
import Avatar from './Avatar';

// Profile-photo picker for a staff member or client. `entity` is the API segment
// ('staff' or 'clients'); `id` is the record id. Uploads are downscaled in the
// browser, POSTed as multipart, and the returned data URL is bubbled up via
// onChange so the parent can update its preview immediately.
export default function ProfilePhotoUpload({ entity, id, photo, firstName, lastName, gradient, onChange }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
    setBusy(true);
    try {
      const blob = await resizeImageFile(file, 400, 0.85);
      const fd = new FormData();
      fd.append('photo', new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
      const { data } = await api.post(`/${entity}/${id}/photo`, fd);
      onChange?.(data.profile_photo_url || null);
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Photo upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.delete(`/${entity}/${id}/photo`);
      onChange?.(null);
      toast.success('Photo removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not remove photo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative shrink-0">
        <Avatar
          photo={photo}
          firstName={firstName}
          lastName={lastName}
          size="w-16 h-16"
          rounded="rounded-2xl"
          text="text-xl font-bold"
          gradient={gradient}
        />
        <button
          type="button"
          onClick={() => !busy && fileRef.current?.click()}
          disabled={busy}
          title="Change photo"
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center shadow hover:bg-primary-600 disabled:opacity-50"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
        </button>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="text-xs font-semibold text-primary-600 hover:text-primary-700 text-left disabled:opacity-50"
        >
          {photo ? 'Change photo' : 'Upload photo'}
        </button>
        {photo && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            className="text-xs font-medium text-red-500 hover:text-red-600 text-left flex items-center gap-1 disabled:opacity-50"
          >
            <Trash2 size={11} /> Remove
          </button>
        )}
        <p className="text-[11px] text-slate-400">JPG, PNG or WebP · auto-resized</p>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}
